import { cn } from "../../../utils/cn.js";
import { Skeleton } from "../Skeleton/Skeleton.js";
import { cva } from "class-variance-authority";
import * as React from "react";
import { jsx } from "react/jsx-runtime";
import { Avatar } from "@base-ui/react/avatar";
//#region src/components/Avatar/Avatar.tsx
const AvatarContext = React.createContext(null);
function useAvatarContext() {
	const context = React.useContext(AvatarContext);
	if (!context) throw new Error("Avatar parts must be used within <AvatarRoot>.");
	return context;
}
function isImagePending(hasImage, status) {
	return hasImage && status !== "loaded" && status !== "error";
}
const avatarRootVariants = cva(["relative inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full align-middle font-body select-none", "bg-background-brand-primary text-text-brand-button"], {
	defaultVariants: { size: "md" },
	variants: { size: {
		sm: "size-[24px] text-body-xsmall leading-body-xsmall font-heading-medium",
		md: "size-[32px] text-body-small leading-body-small font-heading-medium",
		lg: "size-[48px] text-body-medium leading-body-medium font-heading-medium"
	} }
});
/**
* Groups all avatar parts. Renders a `<span>` element with size variants.
*
* Compose with `AvatarImage`, optional `AvatarLoading`, and `AvatarFallback`.
*
* @param props - Base UI root props plus optional size variant
* @returns The rendered avatar root
*/
function AvatarRoot({ className, size, children, ...props }) {
	const [hasImage, setHasImage] = React.useState(false);
	const [status, setStatus] = React.useState("idle");
	return /* @__PURE__ */ jsx(AvatarContext.Provider, {
		value: {
			hasImage,
			setHasImage,
			setStatus,
			status
		},
		children: /* @__PURE__ */ jsx(Avatar.Root, {
			className: cn(avatarRootVariants({ size }), className),
			...props,
			children
		})
	});
}
/**
* Photo layer for the avatar. Hidden until loaded or on error.
*
* @param props - Base UI image props forwarded to Avatar.Image
* @returns The rendered avatar image
*/
function AvatarImage({ className, onLoadingStatusChange, src, ...props }) {
	const { setHasImage, setStatus } = useAvatarContext();
	React.useLayoutEffect(() => {
		setHasImage(Boolean(src));
		return () => {
			setHasImage(false);
			setStatus("idle");
		};
	}, [
		setHasImage,
		setStatus,
		src
	]);
	return /* @__PURE__ */ jsx(Avatar.Image, {
		className: cn("size-full object-cover", className),
		onLoadingStatusChange: (nextStatus) => {
			setStatus(nextStatus);
			onLoadingStatusChange?.(nextStatus);
		},
		src,
		...props
	});
}
/**
* Fallback shown when no image is provided or when loading fails.
*
* Stays mounted while an image loads so consumers without `AvatarLoading`
* keep showing initials. When `AvatarLoading` is present, it overlays this
* layer via z-index during the pending state.
*
* @param props - Base UI fallback props
* @returns The rendered fallback
*/
function AvatarFallback({ className, ...props }) {
	return /* @__PURE__ */ jsx(Avatar.Fallback, {
		className: cn("m-0 flex size-full items-center justify-center", className),
		...props
	});
}
/**
* Placeholder shown while an `AvatarImage` is pending.
*
* Renders above `AvatarFallback` via z-index so initials stay mounted for
* consumers that omit this part. Defaults to a full-bleed `Skeleton`.
*
* @param props - Standard span props
* @returns The loading placeholder, or null once the image loads or fails
*/
function AvatarLoading({ children, className, ...props }) {
	const { hasImage, status } = useAvatarContext();
	if (!isImagePending(hasImage, status)) return null;
	return /* @__PURE__ */ jsx("span", {
		"aria-hidden": true,
		className: cn("absolute inset-0 z-10", className),
		"data-slot": "avatar-loading",
		...props,
		children: children ?? /* @__PURE__ */ jsx(Skeleton, {
			className: "size-full rounded-[inherit]",
			radius: "none"
		})
	});
}
//#endregion
export { AvatarFallback, AvatarImage, AvatarLoading, AvatarRoot, avatarRootVariants };

//# sourceMappingURL=Avatar.js.map